'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Keep the Three.js voice ring out of the initial homepage critical path.
 * Pointer/touch/focus intent upgrades immediately. Desktop also enhances at
 * idle after load; mobile retains the lightweight SVG until actual intent.
 */
export function useDeferredVoiceRing() {
  const [ringInteractive, setRingInteractive] = useState(false);
  const stageRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (ringInteractive) return;
    const node = stageRef.current;
    if (!node) return;

    const upgrade = () => setRingInteractive(true);
    const options: AddEventListenerOptions = { passive: true, once: true };

    node.addEventListener('pointerenter', upgrade, options);
    node.addEventListener('touchstart', upgrade, options);
    node.addEventListener('focusin', upgrade, options);

    return () => {
      node.removeEventListener('pointerenter', upgrade, options);
      node.removeEventListener('touchstart', upgrade, options);
      node.removeEventListener('focusin', upgrade, options);
    };
  }, [ringInteractive]);

  useEffect(() => {
    if (ringInteractive) return;
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 720px)').matches
    ) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;
    let removeLoadListener: (() => void) | undefined;

    const upgrade = () => setRingInteractive(true);
    const requestIdleUpgrade = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(upgrade, { timeout: 3500 });
      } else {
        timeoutId = globalThis.setTimeout(upgrade, 3500);
      }
    };

    if (document.readyState === 'complete') {
      requestIdleUpgrade();
    } else {
      window.addEventListener('load', requestIdleUpgrade, { once: true });
      removeLoadListener = () => window.removeEventListener('load', requestIdleUpgrade);
    }

    return () => {
      removeLoadListener?.();
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [ringInteractive]);

  return {
    ringInteractive,
    stageRef,
    activateRing: () => setRingInteractive(true),
  };
}
